import { t } from 'i18next';
import { Vector3 } from 'three';

import { SettingModelSettingType, type BasePointOfInterest, type Point3D } from 'api/models';
import {
    applyCopilotSetting,
    copilotSettingIds,
    copilotSettingValues,
    readCopilotSettings
} from 'app/modules/wallforming/components/copilot/utils/copilotSettings';
import { inputCommands } from 'app/modules/wallforming/components/input-binding/commands/inputCommands';
import {
    inputActions,
    INPUT_BINDING_SLOTS
} from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import { isInputActionId } from 'app/modules/wallforming/components/input-binding/registry/inputPresetsRegistry';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { InputModifier } from 'app/modules/wallforming/components/input-binding/types/inputModifier';
import {
    formatChord,
    formatChordLabel,
    parseChord
} from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import {
    isContextActive,
    resolveInputContext
} from 'app/modules/wallforming/components/input-binding/utils/inputContextUtils';
import { ProjectsDialog } from 'app/modules/wallforming/components/projects-manager/types/projectDialog';
import { isFillerInstanceSelection } from 'app/modules/wallforming/components/scenes/objects/articles/utils/transformationUtils';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import {
    isCornerSingleSidePoi,
    isPlanarFacePoi
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID, EPSILON } from 'shared/constants';

import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

type Parameter = {
    type: 'string' | 'number' | 'boolean' | 'array';
    enum?: readonly string[];
    minimum?: number;
    maximum?: number;
    maxLength?: number;
    items?: { type: 'string'; maxLength: number };
    maxItems?: number;
};
type Parameters = Record<string, unknown>;

export const normalizeCopilotChord = (chord: string): string | null => {
    const parts = chord
        .trim()
        .split('+')
        .map((part) => part.trim());
    if (parts.length === 0 || parts.some((part) => !part)) {
        return null;
    }
    const modifierNames: Record<string, InputModifier> = {
        ctrl: InputModifier.Ctrl,
        control: InputModifier.Ctrl,
        shift: InputModifier.Shift,
        alt: InputModifier.Alt,
        option: InputModifier.Alt,
        meta: InputModifier.Meta,
        cmd: InputModifier.Meta,
        command: InputModifier.Meta
    };
    const modifiers: InputModifier[] = [];
    for (const part of parts.slice(0, -1)) {
        const modifier = modifierNames[part.toLowerCase()];
        if (!modifier || modifiers.includes(modifier)) {
            return null;
        }
        modifiers.push(modifier);
    }
    const key = parts[parts.length - 1];
    const commonKeyCodes: Record<string, string> = {
        esc: 'Escape',
        escape: 'Escape',
        enter: 'Enter',
        space: 'Space',
        tab: 'Tab',
        backspace: 'Backspace',
        delete: 'Delete',
        del: 'Delete',
        arrowleft: 'ArrowLeft',
        arrowright: 'ArrowRight',
        arrowup: 'ArrowUp',
        arrowdown: 'ArrowDown'
    };
    const code = /^[a-z]$/i.test(key)
        ? `Key${key.toUpperCase()}`
        : /^\d$/.test(key)
          ? `Digit${key}`
          : (commonKeyCodes[key.toLowerCase()] ?? key);
    const supported =
        /^(?:Key[A-Z]|Digit[0-9]|F(?:[1-9]|1[0-9]|2[0-4])|Numpad(?:[0-9]|Add|Subtract|Multiply|Divide|Decimal|Enter|Equal)|Arrow(?:Left|Right|Up|Down)|Escape|Enter|Space|Tab|Backspace|Delete|Insert|Home|End|PageUp|PageDown|CapsLock|NumLock|ScrollLock|Pause|PrintScreen|ContextMenu|Minus|Equal|BracketLeft|BracketRight|Backslash|Semicolon|Quote|Backquote|Comma|Period|Slash|IntlBackslash|Wheel)$/.test(
            code
        ) || parseChord(chord.trim())?.code.startsWith('Mouse');
    if (!supported) {
        return null;
    }
    const canonical = formatChord({ modifiers, code, trigger: undefined });
    return parseChord(canonical) ? canonical : null;
};
interface Capability {
    id: string;
    description: string;
    edits?: boolean;
    parameters: {
        type: 'object';
        properties: Record<string, Parameter>;
        required: string[];
        additionalProperties: false;
    };
    execute: (parameters: Parameters, state: PlacementState) => void | Promise<void>;
}
export interface CopilotAction {
    type: string;
    parameters: Parameters;
}

const text = (parameters: Parameters, name: string): string => {
    const value = parameters[name];
    if (typeof value !== 'string') {
        throw new Error(`Invalid ${name}.`);
    }
    return value;
};
const number = (parameters: Parameters, name: string): number => {
    const value = parameters[name];
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(`Invalid ${name}.`);
    }
    return value;
};
const bool = (parameters: Parameters, name: string): boolean => {
    const value = parameters[name];
    if (typeof value !== 'boolean') {
        throw new Error(`Invalid ${name}.`);
    }
    return value;
};
const strings = (parameters: Parameters, name: string): string[] => {
    const value = parameters[name];
    if (!Array.isArray(value) || !value.every((item): item is string => typeof item === 'string')) {
        throw new Error(`Invalid ${name}.`);
    }
    return value;
};
const enumeration = (values: readonly string[]): Parameter => ({ type: 'string', enum: values });
const boundedNumber = (minimum: number, maximum: number): Parameter => ({ type: 'number', minimum, maximum });
const capability = (
    id: string,
    description: string,
    properties: Record<string, Parameter>,
    execute: Capability['execute'],
    edits = false
): Capability => ({
    id,
    description,
    edits,
    parameters: {
        type: 'object',
        properties,
        required: Object.keys(properties),
        additionalProperties: false
    },
    execute
});

const commandIds = Object.keys(inputActions).filter((id) => isInputActionId(id) && inputActions[id].copilot);
const panelHandlers = {
    group: (open: boolean, state: PlacementState) => state.setIsGroupPanelOpen(open),
    projects: (open: boolean, state: PlacementState) => state.setIsProjectPanelOpen(open),
    saveProject: (open: boolean, state: PlacementState) => {
        state.setIsProjectPanelOpen(open);
        state.setActiveProjectDialog(open ? ProjectsDialog.SAVE : ProjectsDialog.NONE);
    },
    inputBindings: (open: boolean, state: PlacementState) => state.setIsInputBindingPanelOpen(open)
};
const AXIS_INDEX: Readonly<Record<string, 0 | 1 | 2>> = { x: 0, y: 1, z: 2 };
const requireSelection = (state: PlacementState) => {
    if (Object.keys(state.articleInstanceSelections).length === 0) {
        throw new Error(t('copilotNoSelectionToMoveError'));
    }
};
const requirePivot = (state: PlacementState) => {
    requireSelection(state);
    if (!state.arePivotControlsActive || !state.pivotControlsRef.current) {
        throw new Error(t('copilotEnableTransformFirstError'));
    }
};
const requireFillerSelection = (state: PlacementState) => {
    if (!isFillerInstanceSelection(Object.keys(state.articleInstanceSelections), state)) {
        throw new Error(t('copilotScaleFillerSelectionError'));
    }
};

export const copilotCapabilities: readonly Capability[] = [
    capability(
        'interaction_mode',
        'Switch between normal article selection and POI navigation. Use selection before editing article commands. This never enables editing in read-only mode.',
        {
            mode: enumeration(['selection', 'poiNavigation'])
        },
        (parameters, state) =>
            state.setInteractionMode(
                text(parameters, 'mode') === 'selection' ? InteractionMode.DEFAULT : InteractionMode.POI_NAVIGATION
            )
    ),
    capability(
        'run_command',
        'Execute an opted-in application command by its exact actionId from commands. Respects its current interaction context. Transform toggles the pivot; enable it before moving.',
        {
            actionId: enumeration(commandIds)
        },
        (parameters, state) => {
            const id = text(parameters, 'actionId');
            if (!isInputActionId(id) || !inputActions[id].copilot) {
                throw new Error('Command is not permitted.');
            }
            const definition = inputActions[id];
            if (definition.copilot?.edits && state.isViewMode) {
                throw new Error(t('copilotEditModeRequiredError'));
            }
            if (!isContextActive(definition.contexts, resolveInputContext(state))) {
                throw new Error('Command is not available in the current interaction context.');
            }
            if (definition.copilot?.edits) {
                requireSelection(state);
            }
            const command = inputCommands[id];
            if (!command || command(new KeyboardEvent('copilot-command'), state) === false) {
                throw new Error('The command could not run in the current state.');
            }
        }
    ),
    capability(
        'selection',
        'Replace article selection with exact existing instanceIds. An empty array discards selection. Use run_command article-select-all or article-delete for all/selective deletion.',
        {
            instanceIds: { type: 'array', items: { type: 'string', maxLength: 64 }, maxItems: 1000 }
        },
        (parameters, state) => {
            const ids = strings(parameters, 'instanceIds');
            const existing = new Set(
                Object.values(state.articles).flatMap((article) => Object.keys(article.articleInstances))
            );
            if (ids.some((id) => !existing.has(id))) {
                throw new Error('Selection contains missing article instances.');
            }
            state.setArticleInstanceSelections(Object.fromEntries(ids.map((id) => [id, undefined])));
        }
    ),
    capability(
        'select_articles',
        'Select all matching article instances without listing every ID. An empty articleId matches every article; an empty poiId matches the whole session.',
        {
            articleId: { type: 'string', maxLength: 64 },
            poiId: { type: 'string', maxLength: 64 }
        },
        (parameters, state) => {
            const articleId = text(parameters, 'articleId');
            const poiId = text(parameters, 'poiId');
            const poi = poiId ? state.pois.find((data) => data.poi.poiGuid === poiId) : undefined;
            if ((articleId && !state.articles[articleId]) || (poiId && !poi)) {
                throw new Error('Selection scope does not exist.');
            }
            const ids = Object.entries(state.articles)
                .filter(([id]) => !articleId || id === articleId)
                .flatMap(([id, article]) =>
                    Object.keys(article.articleInstances).filter(
                        (instanceId) => !poi || poi.articles[id]?.includes(instanceId)
                    )
                );
            state.setArticleInstanceSelections(Object.fromEntries(ids.map((id) => [id, undefined])));
        }
    ),
    capability(
        'create_group',
        'Create a named group from the current article selection (at least two instances).',
        {
            name: { type: 'string', maxLength: 100 }
        },
        (parameters, state) => {
            const selected = new Set(Object.keys(state.articleInstanceSelections));
            const members = Object.entries(state.articles).flatMap(([articleId, article]) =>
                Object.keys(article.articleInstances)
                    .filter((id) => selected.has(id))
                    .map((instanceId) => ({ articleId, instanceId }))
            );
            if (members.length < 2 || !text(parameters, 'name').trim()) {
                throw new Error('A group needs a name and at least two selected article instances.');
            }
            if (!state.createArticleGroup(text(parameters, 'name'), members)) {
                throw new Error('The group could not be created.');
            }
        },
        true
    ),
    capability(
        'panel',
        'Show or hide a workspace panel. Opening saveProject only opens the normal save dialog; it does not save or overwrite a project.',
        {
            panel: enumeration(Object.keys(panelHandlers)),
            open: { type: 'boolean' }
        },
        (parameters, state) => {
            const handler = Object.entries(panelHandlers).find(([id]) => id === text(parameters, 'panel'))?.[1];
            if (!handler) {
                throw new Error('Panel is unavailable.');
            }
            handler(bool(parameters, 'open'), state);
        }
    ),
    capability(
        'navigate_poi_relative',
        'Navigate to the next or previous Wall or Corner. When already on that type, runs PoiNext/PoiPrevious; otherwise runs directional PoiSwitchType/PoiSwitchTypeReversed. Use this for relative requests, not navigate_poi or an unconditional type switch.',
        {
            poiType: enumeration(Object.values(POIFilterType)),
            direction: enumeration(['next', 'previous'])
        },
        async (parameters, state) => {
            const filterType = Object.values(POIFilterType).find((value) => value === text(parameters, 'poiType'));
            if (!filterType || !state.pois.some((data) => data.filterType === filterType)) {
                throw new Error('No matching POI exists.');
            }
            const reversed = text(parameters, 'direction') === 'previous';
            const current = state.poiSelection.current;
            if (current.index === undefined || current.filterType === undefined) {
                const indices = state.pois.flatMap((data, index) => (data.filterType === filterType ? [index] : []));
                const index = indices[reversed ? indices.length - 1 : 0];
                await state.setPoiSelection(filterType, index);
                if (usePlacementStore.getState().poiSelection.current.index !== index) {
                    throw new Error('POI navigation was declined.');
                }
            } else {
                const actionId =
                    current.filterType === filterType
                        ? reversed
                            ? InputActionId.PoiPrevious
                            : InputActionId.PoiNext
                        : reversed
                          ? InputActionId.PoiSwitchTypeReversed
                          : InputActionId.PoiSwitchType;
                const command = inputCommands[actionId];
                if (!command || command(new KeyboardEvent('copilot-command'), state) === false) {
                    throw new Error('The command could not run in the current state.');
                }
            }
        }
    ),
    capability(
        'navigate_poi',
        'Select an exact POI by poiId, or the first POI of poiType when poiId is empty. For next/previous wall or corner, use navigate_poi_relative instead.',
        {
            poiId: { type: 'string', maxLength: 64 },
            poiType: enumeration(Object.values(POIFilterType))
        },
        async (parameters, state) => {
            const filterType = Object.values(POIFilterType).find((value) => value === text(parameters, 'poiType'));
            const id = text(parameters, 'poiId');
            const index = state.pois.findIndex(
                (data) => data.filterType === filterType && (!id || data.poi.poiGuid === id)
            );
            if (index < 0 || !filterType) {
                throw new Error('No matching POI exists.');
            }
            await state.setPoiSelection(filterType, index);
            if (usePlacementStore.getState().poiSelection.current.index !== index) {
                throw new Error('POI navigation was declined.');
            }
        }
    ),
    capability(
        'placement_view',
        'Change the settings view and active placement category. Automated placement holds solver controls; Catalogue holds manual insertion and article previews.',
        {
            view: enumeration(Object.values(SettingView)),
            settingType: enumeration(Object.values(SettingModelSettingType))
        },
        (parameters, state) => {
            const view = Object.values(SettingView).find((value) => value === text(parameters, 'view'));
            const setting = Object.values(SettingModelSettingType).find(
                (value) => value === text(parameters, 'settingType')
            );
            if (!view || !setting) {
                throw new Error('Invalid placement view.');
            }
            state.setSelectedSettingType(setting);
            state.setSelectedSettingView(view);
        }
    ),
    capability(
        'move_selection',
        'Move selected instances along an active pivot axis in meters. Requires an enabled transform pivot; explicit axis only.',
        {
            axis: enumeration(Object.keys(AXIS_INDEX)),
            meters: boundedNumber(-10, 10)
        },
        (parameters, state) => {
            requirePivot(state);
            state.applyTranslationValue(AXIS_INDEX[text(parameters, 'axis')] ?? 2, number(parameters, 'meters'));
        },
        true
    ),
    capability(
        'rotate_selection',
        'Set selected instance rotation in degrees around the existing pivot normal. Requires the transformation tool.',
        {
            degrees: boundedNumber(-360, 360)
        },
        (parameters, state) => {
            requirePivot(state);
            const rotation = { current: 0 };
            state.handleOnArticleTransformationStart(rotation, { current: false });
            try {
                state.applyRotationValue(2, number(parameters, 'degrees'), rotation);
            } finally {
                state.handleOnArticleTransformationEnd(false);
            }
        },
        true
    ),
    capability(
        'scale_selection',
        'Set the absolute scale factor on one explicit axis for selected filler instances only. Requires an enabled transform pivot.',
        {
            axis: enumeration(Object.keys(AXIS_INDEX)),
            factor: { type: 'number', minimum: EPSILON }
        },
        (parameters, state) => {
            requirePivot(state);
            requireFillerSelection(state);
            state.applyScalingValue(AXIS_INDEX[text(parameters, 'axis')] ?? 2, number(parameters, 'factor'));
        },
        true
    ),
    capability(
        'camera_view',
        'Rotate the camera to a standard view while preserving its current orbit target and distance. World Z is up.',
        {
            view: enumeration(['top', 'bottom', 'front', 'back', 'left', 'right', 'isometric'])
        },
        async (parameters, state) => {
            const controls = state.mainCameraControlsRef.current;
            if (!controls || state.isMainCameraLocked) {
                throw new Error('Camera is unavailable or locked.');
            }
            const directions: Record<string, Vector3> = {
                top: new Vector3(0, -0.0001, 1),
                bottom: new Vector3(0, -0.0001, -1),
                front: new Vector3(0, -1, 0),
                back: new Vector3(0, 1, 0),
                left: new Vector3(-1, 0, 0),
                right: new Vector3(1, 0, 0),
                isometric: new Vector3(1, -1, 1)
            };
            const target = controls.getTarget(new Vector3());
            const position = controls.getPosition(new Vector3());
            const direction = directions[text(parameters, 'view')];
            if (!direction) {
                throw new Error('Invalid camera view.');
            }
            position.copy(target).addScaledVector(direction.normalize(), Math.max(0.1, controls.distance));
            await controls.setLookAt(position.x, position.y, position.z, target.x, target.y, target.z, true);
        }
    ),
    capability(
        'camera_zoom',
        'Zoom by a relative factor (greater than one zooms in, less than one zooms out), preserving camera projection.',
        {
            factor: boundedNumber(0.25, 4)
        },
        async (parameters, state) => {
            const controls = state.mainCameraControlsRef.current;
            if (!controls || state.isMainCameraLocked) {
                throw new Error('Camera is unavailable or locked.');
            }
            const camera = controls.camera;
            if ('isOrthographicCamera' in camera && camera.isOrthographicCamera) {
                await controls.zoomTo(camera.zoom * number(parameters, 'factor'), true);
            } else {
                await controls.dollyTo(controls.distance / number(parameters, 'factor'), true);
            }
        }
    ),
    capability(
        'camera_orbit',
        'Orbit the camera by relative horizontal and vertical angles in degrees. Positive horizontal angle rotates right around the target.',
        {
            horizontalDegrees: boundedNumber(-180, 180),
            verticalDegrees: boundedNumber(-90, 90)
        },
        async (parameters, state) => {
            const controls = state.mainCameraControlsRef.current;
            if (!controls || state.isMainCameraLocked) {
                throw new Error('Camera is unavailable or locked.');
            }
            await controls.rotate(
                (number(parameters, 'horizontalDegrees') * Math.PI) / 180,
                (number(parameters, 'verticalDegrees') * Math.PI) / 180,
                true
            );
        }
    ),
    capability(
        'change_setting',
        'Change one whitelisted display/analysis setting to one of its predefined options (see application settings for current values and allowed options per setting). Display-only; never edits placements.',
        {
            setting: enumeration(copilotSettingIds),
            value: enumeration(copilotSettingValues)
        },
        (parameters, state) => applyCopilotSetting(state, text(parameters, 'setting'), text(parameters, 'value'))
    ),
    capability(
        'input_binding',
        'Request a binding edit using the existing conflict/profile confirmation UI. Uses exact actionId, zero-based slot, and canonical chord (Ctrl+KeyZ). Empty chord unbinds. Does not bypass confirmation.',
        {
            actionId: enumeration(Object.keys(inputActions)),
            slot: boundedNumber(0, INPUT_BINDING_SLOTS - 1),
            chord: { type: 'string', maxLength: 64 }
        },
        (parameters, state) => {
            const id = text(parameters, 'actionId');
            const slot = number(parameters, 'slot');
            const rawChord = text(parameters, 'chord');
            const chord = rawChord ? normalizeCopilotChord(rawChord) : null;
            if (
                !isInputActionId(id) ||
                !Number.isInteger(slot) ||
                slot >= inputActions[id].slots ||
                (rawChord && !chord)
            ) {
                throw new Error('Invalid input binding.');
            }
            state.assignInputBinding(id, slot, chord);
        }
    ),
    capability(
        'panel_gap',
        'Change the panel solver vertical-gap requirement in automated placement settings.',
        {
            required: { type: 'boolean' }
        },
        (parameters, state) => state.setAutomatedPanelRequireVerticalGap(bool(parameters, 'required')),
        true
    )
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

export function validateCopilotActions(value: unknown): CopilotAction[] {
    if (!Array.isArray(value) || value.length < 1 || value.length > 16) {
        throw new Error(t('copilotInvalidActionError'));
    }
    return value.map((action) => {
        if (
            !isRecord(action) ||
            typeof action.type !== 'string' ||
            !isRecord(action.parameters) ||
            Object.keys(action).some((key) => key !== 'type' && key !== 'parameters')
        ) {
            throw new Error(t('copilotInvalidActionError'));
        }
        const definition = copilotCapabilities.find(({ id }) => id === action.type);
        if (!definition) {
            throw new Error(t('copilotInvalidActionError'));
        }
        const parameters = action.parameters;
        if (
            Object.keys(parameters).some((key) => !(key in definition.parameters.properties)) ||
            definition.parameters.required.some((key) => !(key in parameters))
        ) {
            throw new Error(t('copilotInvalidActionError'));
        }
        for (const [key, spec] of Object.entries(definition.parameters.properties)) {
            const item = parameters[key];
            const valid =
                spec.type === 'array'
                    ? Array.isArray(item) &&
                      item.length <= (spec.maxItems ?? 0) &&
                      item.every((entry) => typeof entry === 'string' && entry.length <= (spec.items?.maxLength ?? 0))
                    : spec.type === 'number'
                      ? typeof item === 'number' &&
                        Number.isFinite(item) &&
                        item >= (spec.minimum ?? -Infinity) &&
                        item <= (spec.maximum ?? Infinity)
                      : spec.type === 'boolean'
                        ? typeof item === 'boolean'
                        : typeof item === 'string' &&
                          item.length <= (spec.maxLength ?? 100) &&
                          (!spec.enum || spec.enum.includes(item));
            if (!valid) {
                throw new Error(t('copilotInvalidActionError'));
            }
        }
        return { type: action.type, parameters };
    });
}

export async function executeCopilotActions(value: unknown): Promise<void> {
    const actions = validateCopilotActions(value);
    await runCopilotActions(actions);
}

async function runCopilotActions(actions: CopilotAction[]): Promise<void> {
    const sessionId = usePlacementStore.getState().sessionId;
    for (const [index, action] of actions.entries()) {
        const definition = copilotCapabilities.find(({ id }) => id === action.type)!;
        try {
            if (
                action.type === 'move_selection' ||
                action.type === 'rotate_selection' ||
                action.type === 'scale_selection'
            ) {
                const deadline = performance.now() + 1000;
                while (
                    usePlacementStore.getState().arePivotControlsActive &&
                    !usePlacementStore.getState().pivotControlsRef.current &&
                    performance.now() < deadline
                ) {
                    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
                }
            }
            const state = usePlacementStore.getState();
            if (state.sessionId !== sessionId) {
                throw new Error(t('copilotStaleContextError'));
            }
            if (state.isDraggingActive() || state.isTransformationActiveRef.current || state.inputCaptureTarget) {
                throw new Error('Finish the active interaction before applying Copilot actions.');
            }
            if (definition.edits && state.isViewMode) {
                throw new Error(t('copilotEditModeRequiredError'));
            }
            await definition.execute(action.parameters, state);
            if (
                state.inputBindingDialog ||
                usePlacementStore.getState().inputBindingDialog ||
                usePlacementStore.getState().inputProfileEditorRequest
            ) {
                if (index < actions.length - 1) {
                    throw new Error(
                        'Complete the binding confirmation dialog before requesting the remaining actions.'
                    );
                }
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : t('copilotApplyError');
            throw Object.assign(
                new Error(
                    `Stopped at action ${index + 1} of ${actions.length}. ${index} earlier actions completed. ${message}`
                ),
                { cause: error }
            );
        }
    }
}

const normalizeCopilotRequest = (prompt: string) =>
    prompt
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
const normalizeCopilotActionId = (actionId: string) => actionId.toLowerCase().replace(/[^a-z0-9]/g, '');

const getMentionedInputActionIds = (prompt: string, availableIds: string[]) => {
    const normalizedPrompt = normalizeCopilotRequest(prompt);
    const promptWords = new Set(normalizedPrompt.split(/[^a-z0-9]+/).filter(Boolean));
    const matches = availableIds.map((id) => {
        const normalizedId = normalizeCopilotActionId(id);
        if (normalizedPrompt.replace(/[^a-z0-9]/g, '').includes(normalizedId)) {
            return { id, score: Number.POSITIVE_INFINITY };
        }

        const actionWords = id
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .split(/[^a-zA-Z0-9]+/)
            .map((word) => word.toLowerCase())
            .filter((word) => word.length > 2 && !['article', 'action', 'input'].includes(word));
        return { id, score: actionWords.filter((word) => promptWords.has(word)).length };
    });
    const bestScore = Math.max(...matches.map(({ score }) => score));
    return bestScore > 0 ? matches.filter(({ score }) => score === bestScore).map(({ id }) => id) : [];
};

export const getCopilotCapabilityCatalog = (prompt: string) => {
    const normalizedPrompt = normalizeCopilotRequest(prompt);
    const capabilityIds = new Set<string>();
    const add = (...ids: string[]) => ids.forEach((id) => capabilityIds.add(id));
    const hasShortcutIntent =
        /\b(shortcut|hotkey|key binding|input binding|keyboard binding|tastaturbelegung|tastenkurzel)\b/.test(
            normalizedPrompt
        );
    const inputActionIds = getMentionedInputActionIds(normalizedPrompt, Object.keys(inputActions));
    if (hasShortcutIntent && inputActionIds.length > 0) {
        add('input_binding');
    }

    const mentionedCommandIds = getMentionedInputActionIds(normalizedPrompt, commandIds);

    if (!hasShortcutIntent) {
        const isTransformToolRequest =
            /\b(show|open|enable|activate|toggle)\b/.test(normalizedPrompt) &&
            /\b(translation|rotation|transform|pivot)\b/.test(normalizedPrompt) &&
            /\b(tool|pivot)\b/.test(normalizedPrompt);
        const hasPoiTarget = /\b(wall|corner|poi)\b/.test(normalizedPrompt);
        if (
            /\b(selection|deselect|discard|unselect)\b/.test(normalizedPrompt) ||
            (/\b(select)\b/.test(normalizedPrompt) && !hasPoiTarget)
        ) {
            add('selection', 'select_articles');
        }
        if (/\b(group|grouping)\b/.test(normalizedPrompt)) {
            add('create_group');
        }
        if (/\bungroup\b/.test(normalizedPrompt)) {
            add('run_command');
        }
        if (
            /\b(panel|project|input bindings?)\b/.test(normalizedPrompt) &&
            /\b(open|close|show|hide)\b/.test(normalizedPrompt)
        ) {
            add('panel');
        }
        if (hasPoiTarget) {
            if (/\b(next|previous|prev)\b/.test(normalizedPrompt)) {
                add('navigate_poi_relative');
            } else if (/\b(navigate|switch|select|go to)\b/.test(normalizedPrompt)) {
                add('navigate_poi');
            }
        }
        if (/\b(catalogue|catalog|placement tab|automated placement settings)\b/.test(normalizedPrompt)) {
            add('placement_view');
        }

        const hasCameraIntent =
            /\b(camera|zoom|orbit)\b/.test(normalizedPrompt) ||
            /\b(top|bottom|front|back|left|right|isometric)\s+view\b/.test(normalizedPrompt);
        if (
            /\b(move|movement|translate|translation)\b/.test(normalizedPrompt) &&
            !hasCameraIntent &&
            !isTransformToolRequest
        ) {
            add('move_selection', 'run_command');
        }
        if (/\b(rotate|rotation)\b/.test(normalizedPrompt) && !hasCameraIntent && !isTransformToolRequest) {
            add('rotate_selection', 'run_command');
        }
        if (/\b(scale|scaling)\b/.test(normalizedPrompt) && !isTransformToolRequest) {
            add('scale_selection', 'run_command');
        }
        if (hasCameraIntent) {
            if (/\b(camera view|view camera|top|bottom|front|back|left|right|isometric)\b/.test(normalizedPrompt)) {
                add('camera_view');
            }
            if (/\b(zoom|closer|farther|further)\b/.test(normalizedPrompt)) {
                add('camera_zoom');
            }
            if (/\b(orbit|rotate|rotation)\b/.test(normalizedPrompt)) {
                add('camera_orbit');
            }
        }
        if (/\b(setting|settings|display|color|colour|opacity|wireframe|grid)\b/.test(normalizedPrompt)) {
            add('change_setting');
        }
        if (
            /\b(panel gap|vertical gap|gap requirement)\b/.test(normalizedPrompt) &&
            /\b(setting|settings|change|set|toggle|enable|disable)\b/.test(normalizedPrompt)
        ) {
            add('panel_gap');
        }
        if (/\b(mode|poi navigation)\b/.test(normalizedPrompt)) {
            add('interaction_mode');
        }

        const needsTransformTool =
            isTransformToolRequest ||
            /\b(move|movement|translate|translation|scale|scaling|transform|pivot)\b/.test(normalizedPrompt) ||
            (/\b(rotate|rotation)\b/.test(normalizedPrompt) && !hasCameraIntent);
        if (isTransformToolRequest) {
            const transformCommand = commandIds.find((id) => normalizeCopilotActionId(id).includes('articletransform'));
            for (let index = mentionedCommandIds.length - 1; index >= 0; index--) {
                if (!normalizeCopilotActionId(mentionedCommandIds[index]).includes('articletransform')) {
                    mentionedCommandIds.splice(index, 1);
                }
            }
            if (transformCommand && !mentionedCommandIds.includes(transformCommand)) {
                mentionedCommandIds.push(transformCommand);
            }
        }
        const hasDirectCommandIntent = /\b(undo|redo|delete|cut|copy|transform|pivot|ungroup)\b/.test(normalizedPrompt);
        if (needsTransformTool) {
            const transformCommand = commandIds.find((id) => normalizeCopilotActionId(id).includes('articletransform'));
            if (transformCommand && !mentionedCommandIds.includes(transformCommand)) {
                mentionedCommandIds.push(transformCommand);
            }
        }
        if (mentionedCommandIds.length > 0 && (hasDirectCommandIntent || needsTransformTool)) {
            add('run_command');
        }
    }

    return copilotCapabilities
        .filter(({ id }) => capabilityIds.has(id))
        .map(({ id, description, parameters }) => {
            if (id === 'run_command') {
                return {
                    id,
                    description,
                    parameters: {
                        ...parameters,
                        properties: {
                            ...parameters.properties,
                            actionId: { ...parameters.properties.actionId, enum: mentionedCommandIds }
                        }
                    }
                };
            }
            if (id === 'input_binding') {
                return {
                    id,
                    description,
                    parameters: {
                        ...parameters,
                        properties: {
                            ...parameters.properties,
                            actionId: { ...parameters.properties.actionId, enum: inputActionIds }
                        }
                    }
                };
            }
            return { id, description, parameters };
        });
};

export const getCopilotRelevantCommandIds = (prompt: string): string[] | undefined => {
    const normalizedPrompt = normalizeCopilotRequest(prompt);
    const hasTransformIntent =
        /\b(move|movement|translate|translation|rotate|rotation|scale|scaling|transform|pivot)\b/.test(
            normalizedPrompt
        ) && !/\b(camera|zoom|orbit)\b/.test(normalizedPrompt);
    const hasCommandContextIntent =
        hasTransformIntent ||
        /\b(shortcut|hotkey|key binding|input binding|keyboard binding|tastaturbelegung|tastenkurzel|command|commands|undo|redo|delete|cut|copy|ungroup)\b/.test(
            normalizedPrompt
        );
    if (!hasCommandContextIntent) {
        return [];
    }

    const mentionedActionIds = getMentionedInputActionIds(normalizedPrompt, Object.keys(inputActions));
    if (mentionedActionIds.length > 0) {
        return mentionedActionIds;
    }
    if (hasTransformIntent) {
        const transformCommand = commandIds.find((id) => normalizeCopilotActionId(id).includes('articletransform'));
        if (transformCommand) {
            return [transformCommand];
        }
    }
    return undefined;
};

const toPoint = (vector: Vector3): Point3D => ({ x: vector.x, y: vector.y, z: vector.z });

const toUnitVector = (point: Point3D): Vector3 | null => {
    const vector = new Vector3(point.x, point.y, point.z);
    return vector.lengthSq() > EPSILON * EPSILON ? vector.normalize() : null;
};

const getProjectedRange = (points: Point3D[], axis: Vector3): number | null => {
    if (points.length === 0) {
        return null;
    }

    let minimum = Number.POSITIVE_INFINITY;
    let maximum = Number.NEGATIVE_INFINITY;
    for (const point of points) {
        const projection = new Vector3(point.x, point.y, point.z).dot(axis);
        if (!Number.isFinite(projection)) {
            return null;
        }
        minimum = Math.min(minimum, projection);
        maximum = Math.max(maximum, projection);
    }

    return maximum - minimum;
};

function buildSelectedPoiGeometry(poi: BasePointOfInterest | undefined) {
    if (!poi) {
        return null;
    }

    if (isPlanarFacePoi(poi)) {
        const edges = poi.formFace.outerWire.edges;
        const boundaryPoints = edges.map((edge) => edge.startPoint);
        const firstEdge = edges[0];
        const widthAxis = firstEdge
            ? toUnitVector({
                  x: firstEdge.endPoint.x - firstEdge.startPoint.x,
                  y: firstEdge.endPoint.y - firstEdge.startPoint.y,
                  z: firstEdge.endPoint.z - firstEdge.startPoint.z
              })
            : null;
        const normal = toUnitVector(poi.formFace.normal);
        const heightAxis = widthAxis && normal ? new Vector3().crossVectors(normal, widthAxis).normalize() : null;

        return {
            poiId: poi.poiGuid,
            type: 'wall',
            units: 'm',
            widthMeters: widthAxis ? getProjectedRange(boundaryPoints, widthAxis) : null,
            heightMeters: heightAxis ? getProjectedRange(boundaryPoints, heightAxis) : null,
            normal: normal ? toPoint(normal) : null
        };
    }

    if (isCornerSingleSidePoi(poi)) {
        const topPoint = new Vector3(poi.topPoint.x, poi.topPoint.y, poi.topPoint.z);
        const bottomPoint = new Vector3(poi.bottomPoint.x, poi.bottomPoint.y, poi.bottomPoint.z);
        const faceNormalA = toUnitVector(poi.faceA.formFace.normal);
        const faceNormalB = toUnitVector(poi.faceB.formFace.normal);
        const normal = new Vector3().add(faceNormalA ?? new Vector3()).add(faceNormalB ?? new Vector3());
        const edgeDirection = topPoint.clone().sub(bottomPoint);
        if (edgeDirection.lengthSq() > EPSILON * EPSILON) {
            normal.projectOnPlane(edgeDirection.normalize());
        }
        if (normal.lengthSq() <= EPSILON * EPSILON) {
            normal.copy(faceNormalA ?? faceNormalB ?? new Vector3());
        }

        return {
            poiId: poi.poiGuid,
            type: 'corner',
            units: 'm',
            topPoint: poi.topPoint,
            bottomPoint: poi.bottomPoint,
            heightMeters: topPoint.distanceTo(bottomPoint),
            normal: normal.lengthSq() > EPSILON * EPSILON ? toPoint(normal.normalize()) : null,
            faceNormals: [poi.faceA.formFace.normal, poi.faceB.formFace.normal]
        };
    }

    return null;
}

export function buildCopilotApplicationContext(
    state: PlacementState,
    selectedPoiIndex?: number,
    includeWorkspaceDetails = true,
    commandIds?: readonly string[],
    prompt = ''
) {
    const interactionContext = resolveInputContext(state);
    const normalizedPrompt = normalizeCopilotRequest(prompt);
    const includes = (pattern: RegExp) => pattern.test(normalizedPrompt);
    const poiIntent = includes(
        /\b(wall|walls|corner|corners|poi|geometry|dimension|dimensions|formwork|placement|concrete|pressure|load|force|shear|tension|moment|stability|engineering|solver)\b/
    );
    const commandIntent = commandIds === undefined || commandIds.length > 0;
    const settingsIntent = includes(
        /\b(setting|settings|display|color|colour|opacity|wireframe|grid|heatmap|shadow|reflection|fps|unit|units)\b/
    );
    const automatedPlacementIntent = includes(/\b(automated placement|solver|panel gap|vertical gap)\b/);
    const cameraIntent =
        includes(/\b(camera|zoom|orbit)\b/) ||
        /\b(top|bottom|front|back|left|right|isometric)\s+view\b/.test(normalizedPrompt);
    const articleIntent = includes(/\b(article|articles|catalogue|catalog)\b/);
    const groupIntent = includes(/\b(group|groups|grouping|ungroup)\b/);
    const panelIntent = includes(/\b(panel|project|input binding|input bindings)\b/);
    const activePoiIndex = selectedPoiIndex ?? state.poiSelection.current.index;
    const selectedPoi = activePoiIndex === undefined ? undefined : state.pois[activePoiIndex]?.poi;
    const poiCounts = poiIntent
        ? state.pois.reduce(
              (counts, { poi, filterType }) => {
                  if (poi.poiGuid !== EMPTY_GUID) {
                      counts.total += 1;
                      if (filterType === POIFilterType.Wall) {
                          counts.walls += 1;
                      } else if (filterType === POIFilterType.Corner) {
                          counts.corners += 1;
                      }
                  }
                  return counts;
              },
              { total: 0, walls: 0, corners: 0 }
          )
        : undefined;
    const applicationContext: Record<string, unknown> = { readOnly: state.isViewMode };
    if (poiCounts) {
        applicationContext.poiCounts = poiCounts;
    }
    if (includeWorkspaceDetails && poiIntent) {
        applicationContext.selectedPoiGeometry = buildSelectedPoiGeometry(selectedPoi);
    }
    if (includeWorkspaceDetails && commandIntent) {
        applicationContext.interactionContext = interactionContext;
        applicationContext.selectedPoi =
            activePoiIndex === undefined ? state.poiSelection.current : { index: activePoiIndex };
        applicationContext.selectionCount = Object.keys(state.articleInstanceSelections).length;
    }
    if (includeWorkspaceDetails && settingsIntent) {
        applicationContext.selectedSettingType = state.selectedSettingType;
        applicationContext.selectedSettingView = state.selectedSettingView;
        applicationContext.settings = readCopilotSettings(state);
    }
    if (includeWorkspaceDetails && automatedPlacementIntent) {
        applicationContext.automatedPlacement = state.automatedPlacementSettingsByType;
    }
    if (includeWorkspaceDetails && cameraIntent) {
        applicationContext.camera = state.mainCameraSettings;
        applicationContext.cameraLocked = state.isMainCameraLocked;
    }
    if (includeWorkspaceDetails && articleIntent) {
        applicationContext.articles = Object.entries(state.articles)
            .slice(0, 100)
            .map(([id, article]) => ({
                id,
                articleNumber: article.articleNumber,
                instanceCount: Object.keys(article.articleInstances).length
            }));
        applicationContext.summariesTruncated = Object.keys(state.articles).length > 100;
    }
    if (includeWorkspaceDetails && groupIntent) {
        applicationContext.groups = Object.values(state.articleGroups)
            .slice(0, 50)
            .map(({ id, name, members }) => ({ id, name: name.slice(0, 100), count: members.length }));
    }
    if (includeWorkspaceDetails && panelIntent) {
        applicationContext.panels = {
            group: state.isGroupPanelOpen,
            projects: state.isProjectPanelOpen,
            inputBindings: state.isInputBindingPanelOpen
        };
    }
    if (includeWorkspaceDetails && commandIntent) {
        const commands = Object.entries(inputActions)
            .filter(([id]) => commandIds === undefined || commandIds.includes(id))
            .map(([id, action]) => ({
                actionId: id,
                label: t(action.labelKey),
                category: action.category,
                available: isContextActive(action.contexts, interactionContext),
                shortcuts: isInputActionId(id)
                    ? state.inputBindings.slots[id].map((chord) => (chord ? formatChordLabel(chord) : null))
                    : [],
                copilotExecutable: Boolean(action.copilot),
                edits: action.copilot?.edits
            }));
        if (commands.length > 0) {
            applicationContext.commands = commands;
        }
    }
    return applicationContext;
}
